/**
 * Chart layer
 * ---------------------------------------------------------------------------
 * One theme, one set of marks, used by every analytics screen.
 *
 * The categorical order below passed all six checks of the palette validator
 * against the light chart surface (lightness band, chroma floor, CVD separation,
 * normal-vision floor, contrast). Hues are assigned in this fixed order and are
 * never cycled or reordered per chart - colour follows the entity, not its rank.
 *
 *   validate_palette.js "#2354F0,#AE730B,#0E9B8E,#6D5AE6,#0A9467" --mode light
 *   → ALL CHECKS PASS
 *
 * Red (#CE4630) is deliberately absent: it is reserved for status, and always
 * ships with a label or icon rather than carrying meaning by colour alone.
 */

import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

/** Fixed categorical order - never cycled. A 6th series folds into "Other". */
export const SERIES = ['#2354F0', '#AE730B', '#0E9B8E', '#6D5AE6', '#0A9467'];

/** Reserved status colours - used with a label, never colour alone. */
export const STATUS = {
  good: '#0A9467',
  warning: '#AE730B',
  serious: '#CE4630',
  critical: '#963120',
  idle: '#98A2A8',
};

const INK = '#69747A';
const GRID = 'rgba(14,18,22,.07)';

const axis = {
  tick: { fill: INK, fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif" },
  axisLine: { stroke: GRID },
  tickLine: false as const,
};

const tooltipStyle = {
  contentStyle: {
    background: '#fff',
    border: '1px solid rgba(14,18,22,.09)',
    borderRadius: 11,
    boxShadow: '0 3px 14px rgba(18,24,34,.055)',
    fontSize: 11.5,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    padding: '8px 11px',
  },
  labelStyle: { fontWeight: 700, color: '#0E1216', marginBottom: 4 },
  cursor: { stroke: 'rgba(14,18,22,.18)', strokeWidth: 1 },
};

const legendStyle = {
  wrapperStyle: {
    fontSize: 11,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: INK,
    paddingTop: 6,
  },
  iconType: 'plainline' as const,
  iconSize: 10,
};

export interface Series {
  key: string;
  label: string;
}

/* ------------------------------------------------------------------ frame */

export const ChartFrame: React.FC<{
  title: string;
  sub?: string;
  right?: React.ReactNode;
  height?: number;
  children: React.ReactNode;
}> = ({ title, sub, right, height = 230, children }) => (
  <div className="card">
    <div className="card-hd">
      <h4>{title}</h4>
      {sub && <span className="s">{sub}</span>}
      {right && <div className="r">{right}</div>}
    </div>
    <div className="card-b" style={{ paddingTop: 8 }}>
      <div style={{ width: '100%', height }}>{children}</div>
    </div>
  </div>
);

/* ------------------------------------------------------------- line chart */

export const Trend: React.FC<{
  data: any[];
  x: string;
  series: Series[];
  height?: number;
  unit?: string;
}> = ({ data, x, series, unit }) => (
  <ResponsiveContainer width="100%" height="100%">
    <LineChart data={data} margin={{ top: 6, right: 12, left: -14, bottom: 0 }}>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey={x} {...axis} />
      <YAxis {...axis} unit={unit} width={54} domain={['auto', 'auto']} />
      <Tooltip {...tooltipStyle} />
      {series.length > 1 && <Legend {...legendStyle} />}
      {series.map((s, i) => (
        <Line
          key={s.key}
          type="monotone"
          dataKey={s.key}
          name={s.label}
          stroke={SERIES[i % SERIES.length]}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
        />
      ))}
    </LineChart>
  </ResponsiveContainer>
);

/* ------------------------------------------------------------- area chart */

export const AreaTrend: React.FC<{
  data: any[];
  x: string;
  series: Series[];
  unit?: string;
}> = ({ data, x, series, unit }) => (
  <ResponsiveContainer width="100%" height="100%">
    <AreaChart data={data} margin={{ top: 6, right: 12, left: -14, bottom: 0 }}>
      <defs>
        {series.map((s, i) => (
          <linearGradient id={`fill-${s.key}`} key={s.key} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={SERIES[i % SERIES.length]} stopOpacity={0.18} />
            <stop offset="100%" stopColor={SERIES[i % SERIES.length]} stopOpacity={0.02} />
          </linearGradient>
        ))}
      </defs>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey={x} {...axis} />
      <YAxis {...axis} unit={unit} width={54} domain={['auto', 'auto']} />
      <Tooltip {...tooltipStyle} />
      {series.length > 1 && <Legend {...legendStyle} />}
      {series.map((s, i) => (
        <Area
          key={s.key}
          type="monotone"
          dataKey={s.key}
          name={s.label}
          stroke={SERIES[i % SERIES.length]}
          strokeWidth={2}
          fill={`url(#fill-${s.key})`}
        />
      ))}
    </AreaChart>
  </ResponsiveContainer>
);

/* -------------------------------------------------------------- bar chart */

export const Bars: React.FC<{
  data: any[];
  x: string;
  series: Series[];
  stacked?: boolean;
  horizontal?: boolean;
  colorBy?: (row: any, i: number) => string;
  unit?: string;
}> = ({ data, x, series, stacked, horizontal, colorBy, unit }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart
      data={data}
      layout={horizontal ? 'vertical' : 'horizontal'}
      margin={{ top: 6, right: 14, left: horizontal ? 8 : -14, bottom: 0 }}
      barCategoryGap={horizontal ? '28%' : '32%'}
    >
      <CartesianGrid stroke={GRID} vertical={horizontal} horizontal={!horizontal} />
      {horizontal ? (
        <>
          <XAxis type="number" {...axis} unit={unit} />
          <YAxis type="category" dataKey={x} {...axis} width={118} />
        </>
      ) : (
        <>
          <XAxis dataKey={x} {...axis} />
          <YAxis {...axis} unit={unit} width={54} />
        </>
      )}
      <Tooltip {...tooltipStyle} cursor={{ fill: 'rgba(14,18,22,.04)' }} />
      {series.length > 1 && <Legend {...legendStyle} iconType="square" />}
      {series.map((s, i) => (
        <Bar
          key={s.key}
          dataKey={s.key}
          name={s.label}
          stackId={stacked ? 'a' : undefined}
          fill={SERIES[i % SERIES.length]}
          radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
          /* 2px surface gap between stacked segments */
          stroke={stacked ? '#fff' : undefined}
          strokeWidth={stacked ? 2 : 0}
          maxBarSize={horizontal ? 18 : 34}
        >
          {colorBy &&
            data.map((row, j) => <Cell key={j} fill={colorBy(row, j)} />)}
        </Bar>
      ))}
    </BarChart>
  </ResponsiveContainer>
);

/* -------------------------------------------------------------- sparkline */

export const Sparkline: React.FC<{ data: number[]; color?: string; height?: number }> = ({
  data,
  color = SERIES[0],
  height = 28,
}) => (
  <div style={{ width: 90, height }}>
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data.map((v, i) => ({ i, v }))} margin={{ top: 3, right: 2, bottom: 3, left: 2 }}>
        <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  </div>
);
